import {
  Box,
  Button,
  Center,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
} from "@chakra-ui/icons";
import { useCallback, useEffect, useState } from "react";
import { saveAs } from "file-saver";
import { format } from "date-fns";
import { Attachment, Transaction } from "../../../API";
import { fetchReceipt } from "../../../utils";

// Only the fields actually consumed.
type ReceiptTransactionFields = Pick<
  Transaction,
  "id" | "title" | "paidAt" | "currencyCode" | "amount"
>;

type ReceiptLightboxProps = {
  attachments: Pick<Attachment, "id" | "key">[];
  initialIndex: number;
  transaction: ReceiptTransactionFields;
  isOpen: boolean;
  onClose: () => void;
};

const isPdf = (key: string) => key.toLowerCase().endsWith(".pdf");

const buildFilename = (
  transaction: ReceiptTransactionFields,
  attachmentKey: string
): string => {
  const date = format(new Date(transaction.paidAt), "yyyy-MM-dd");
  const currency = transaction.currencyCode.toUpperCase();
  const amount = transaction.amount.toFixed(2);
  // Allow alphanumerics, German umlauts, whitespace, hyphen, apostrophe
  // (common in titles like "L'Auberge", "Pop-Up").
  const titleSafe = (transaction.title ?? "")
    .replace(
      /[^a-zA-Z0-9\u00E4\u00F6\u00FC\u00C4\u00D6\u00DC\u00DF\s'\-]/g,
      ""
    )
    .trim()
    .substring(0, 25)
    .replace(/\s+/g, "_");
  const ext = attachmentKey.split(".").pop() ?? "jpg";
  return `Beleg_${date}_${currency}_${amount}_${titleSafe}.${ext}`;
};

const ReceiptLightbox = ({
  attachments,
  initialIndex,
  transaction,
  isOpen,
  onClose,
}: ReceiptLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "loaded" | "error">(
    "loading"
  );
  const [isDownloading, setIsDownloading] = useState(false);

  // Sync currentIndex with initialIndex when lightbox opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  const activeAttachment = attachments[currentIndex];

  // Fetch blob for active attachment via S3 pre-signed URL
  useEffect(() => {
    if (!isOpen || !activeAttachment) return;

    setBlobUrl(null);
    setLoadState("loading");

    let createdUrl: string | null = null;
    let cancelled = false;

    fetchReceipt(activeAttachment.key).then((data) => {
      if (cancelled) return;
      if (data) {
        createdUrl = URL.createObjectURL(data.blob);
        setBlobUrl(createdUrl);
        setLoadState("loaded");
      } else {
        setLoadState("error");
      }
    });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [isOpen, activeAttachment?.key]);

  const goLeft = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const goRight = useCallback(() => {
    setCurrentIndex((prev) => Math.min(attachments.length - 1, prev + 1));
  }, [attachments.length]);

  // Keyboard navigation: left/right arrows
  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") goLeft();
      if (event.key === "ArrowRight") goRight();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, goLeft, goRight]);

  const handleDownload = async () => {
    if (!activeAttachment) return;
    setIsDownloading(true);

    try {
      const data = await fetchReceipt(activeAttachment.key);
      if (data) {
        saveAs(data.blob, buildFilename(transaction, activeAttachment.key));
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const activeIsPdf = activeAttachment ? isPdf(activeAttachment.key) : false;
  const hasMultiple = attachments.length > 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="transparent" boxShadow="none" maxH="90vh">
        <ModalCloseButton color="white" zIndex={2} />

        {/* Main display area */}
        <Center position="relative" minH="60vh">
          {/* Left arrow */}
          {hasMultiple && (
            <IconButton
              aria-label="Vorheriges Bild"
              icon={<ChevronLeftIcon boxSize="8" />}
              position="absolute"
              left="2"
              top="50%"
              transform="translateY(-50%)"
              zIndex={2}
              variant="ghost"
              color="white"
              _hover={{ bg: "whiteAlpha.200" }}
              isDisabled={currentIndex === 0}
              onClick={goLeft}
              size="lg"
            />
          )}

          {/* Attachment display */}
          <Box width="full" maxH="80vh" px={hasMultiple ? "14" : "4"}>
            {loadState === "loading" && (
              <Skeleton height="60vh" width="full" borderRadius="md" />
            )}
            {loadState === "error" && (
              <Center height="60vh">
                <Text fontSize="md" color="whiteAlpha.700">
                  Beleg konnte nicht geladen werden
                </Text>
              </Center>
            )}
            {loadState === "loaded" && blobUrl && (
              <>
                {activeIsPdf ? (
                  <Box
                    as="iframe"
                    src={blobUrl}
                    width="full"
                    height="80vh"
                    border="none"
                    borderRadius="md"
                    bg="white"
                    title="Beleg PDF"
                  />
                ) : (
                  <Image
                    src={blobUrl}
                    objectFit="contain"
                    maxH="80vh"
                    width="full"
                    alt="Beleg"
                    borderRadius="md"
                  />
                )}
              </>
            )}
          </Box>

          {/* Right arrow */}
          {hasMultiple && (
            <IconButton
              aria-label="Nächstes Bild"
              icon={<ChevronRightIcon boxSize="8" />}
              position="absolute"
              right="2"
              top="50%"
              transform="translateY(-50%)"
              zIndex={2}
              variant="ghost"
              color="white"
              _hover={{ bg: "whiteAlpha.200" }}
              isDisabled={currentIndex === attachments.length - 1}
              onClick={goRight}
              size="lg"
            />
          )}
        </Center>

        {/* Bottom bar */}
        <HStack justifyContent="center" p="3" spacing="4">
          {hasMultiple && (
            <Text color="white" fontSize="sm">
              {currentIndex + 1} / {attachments.length}
            </Text>
          )}
          <Button
            size="sm"
            colorScheme="whiteAlpha"
            leftIcon={<DownloadIcon />}
            onClick={handleDownload}
            isLoading={isDownloading}
          >
            Herunterladen
          </Button>
        </HStack>
      </ModalContent>
    </Modal>
  );
};

export default ReceiptLightbox;
